import styles from '../assets/styles/NewContainerFlow.module.css';
import StepHeader from '../components/StepHeader/StepHeader';
import { DOCKER_LOGO } from '../components/BuildMethodSelector/stackLogos';
// `docker-logo.svg`, not DOCKER_LOGO.Logo: that asset declares no `fill` and
// paints black whatever `color` says. Only the brand hex comes from the
// shared constant.
import { ReactComponent as DockerLogo } from '../assets/images/docker-logo.svg';
import newContainerLogo from "../assets/images/containers-logo.svg";
import {ReactComponent as SearchIcon} from "../assets/images/search-icon.svg";
import { GitHubRepository, Subscription } from '@back4app2/sdk/lib/Back4app2';
import { App, AuthenticationError, AuthorizationError, NetworkError, GitHubCodeError, GitHubInstallationIdError, DuplicatedError, NotFoundError, BlockedResourceError, UserVerificationPendingError, CreditCardValidationRequiredError, InvalidInputError, User } from '@back4app2/sdk';
import back4app2 from '../back4app2';

import { useEffect, useReducer, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import IntegrateGithub, { filterRepositories } from '../components/IntegrateGithub/IntegrateGithub';
import Button from '../components/Button';

import { ReactComponent as GithubIcon } from "../assets/images/github-logo.svg";
import { ReactComponent as B4AIconSVG } from "../assets/images/b4a-sqaure-glow-logo.svg";
import { ContainerType } from '../types/containerTypes';

import CreateContainerForm from './CreateContainerForm';
import SourceSelector, { AppSource } from '../components/SourceSelector/SourceSelector';
import ContainerImageSource, { ContainerImageValue, INITIAL_CONTAINER_IMAGE } from '../components/ContainerImageSource/ContainerImageSource';
import { ContainerImageInfo } from '@back4app2/sdk';

import { BACK4APP_DOT_COM_SITE_URL, BACK4APP_DOT_COM_DASHBOARD_URL } from '../settings';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import CreditCardValidation from '../components/CreditCardValidation';
import PreDeployPanel from '../components/PreDeployPanel';

interface NewContainerFlowState {
  step: number;
  gitHubRepositories: GitHubRepository[] | undefined,
  
  isLoadingRepository: boolean, // do we really need it?
  loadingRepositoryErrorMsg: string | undefined,
  
  isImporting: boolean,
  importingRepoError: string | undefined;
  isImportingSuccess: boolean;
  showCreditCardRequired: boolean;
  
  selectedRepository: GitHubRepository | undefined,
  isCreatingApp: boolean;
  creatingAppErrorMessage: string;
  /** The app that was just created, when its first build is waiting on the
   * pre-deploy screen (step 2). Null on every other path — a clean project
   * goes straight to the overview. */
  createdApp: App | undefined;
};

const INITIAL_STATE:  NewContainerFlowState = {
  step: 0,
  
  gitHubRepositories: undefined,
  isLoadingRepository: true,
  loadingRepositoryErrorMsg: undefined,
  
  isImporting: false,
  importingRepoError: undefined,
  isImportingSuccess: false,
  showCreditCardRequired: false,

  selectedRepository: undefined,

  isCreatingApp: false,
  creatingAppErrorMessage: '',
  createdApp: undefined
};

enum ContainerFlowActionType {
  Reset,
  StartImportingRepository,
  ImportingRepoSuccess,
  ImportingRepoError,
  SetSelectedRepository,
  SetCurrentStep,
  SetGithubRepositories,
  SetFetchGithubRepositoriesError,
  SetContainerName,
  StartCreatingApp,
  FinishCreatingApp,
  ShowCardRequired,
  HoldFirstBuild,
}

const reset = () => ({ type: ContainerFlowActionType.Reset } as const);
const updateCurrentStep = (value: number) => ({ type: ContainerFlowActionType.SetCurrentStep, payload: value } as const);

const setGithubRepositories = (value: GitHubRepository[] | undefined) => ({ type: ContainerFlowActionType.SetGithubRepositories, payload: value } as const);
const setFetchGithubRepositoriesError = (message: string) => ({ type: ContainerFlowActionType.SetFetchGithubRepositoriesError, payload: message } as const);

const startImportingRepository = () => ({ type: ContainerFlowActionType.StartImportingRepository } as const);
const importRepositorySuccess = () => ({ type: ContainerFlowActionType.ImportingRepoSuccess } as const);
const importRepositoryError = (message: string) => ({ type: ContainerFlowActionType.ImportingRepoError, payload: message } as const);
const setShowCreditCardRequired = (message: string) => ({ type: ContainerFlowActionType.ShowCardRequired, payload: message } as const);

const setSelectedRepository = (repo: GitHubRepository) => ({ type: ContainerFlowActionType.SetSelectedRepository, payload: repo } as const);

const setContainerName = (value: string) => ({
  type: ContainerFlowActionType.SetContainerName,
  payload: {
    value
  }
} as const);

const startCreatingApp = () => ({
  type: ContainerFlowActionType.StartCreatingApp,
} as const);

// The app was created and its first build is waiting on the pre-deploy
// screen. Not an error and not a failure to create: the app exists, and this
// is the last step of making it.
const holdFirstBuild = (app: App) => ({
  type: ContainerFlowActionType.HoldFirstBuild,
  payload: app
} as const);

const finishCreatingApp = (errorMessage?: string) => ({
  type: ContainerFlowActionType.FinishCreatingApp,
  payload: {
    errorMessage
  }
} as const);

type NewContainerFlowAction = 
  | ReturnType<typeof reset> 
  | ReturnType<typeof startImportingRepository> 
  | ReturnType<typeof importRepositorySuccess> 
  | ReturnType<typeof importRepositoryError> 
  | ReturnType<typeof setSelectedRepository> 
  | ReturnType<typeof updateCurrentStep>
  | ReturnType<typeof setGithubRepositories>
  | ReturnType<typeof setFetchGithubRepositoriesError>
  | ReturnType<typeof setContainerName>
  | ReturnType<typeof startCreatingApp>
  | ReturnType<typeof finishCreatingApp>
  | ReturnType<typeof holdFirstBuild>
  | ReturnType<typeof setShowCreditCardRequired>;

function reducer(state: NewContainerFlowState, action: NewContainerFlowAction): NewContainerFlowState {
  switch (action.type) {
    case ContainerFlowActionType.Reset:
      return { ...INITIAL_STATE };
    
    case ContainerFlowActionType.StartImportingRepository:
      return { ...state, isImporting: true, gitHubRepositories: undefined, importingRepoError: undefined};

    case ContainerFlowActionType.ImportingRepoSuccess:
      return { ...state, isImporting: false, isImportingSuccess: true };

    case ContainerFlowActionType.ImportingRepoError:
      return { ...state, isImporting: false, importingRepoError: action.payload, isImportingSuccess: false };

    case ContainerFlowActionType.ShowCardRequired:
      return { ...state, isImporting: false, importingRepoError: action.payload, isImportingSuccess: false, showCreditCardRequired: true };

    case ContainerFlowActionType.SetSelectedRepository:
      return {
        ...state,
        selectedRepository: action.payload,
        step: 1
      };

    case ContainerFlowActionType.SetCurrentStep:
      return { ...state, step: action.payload, isCreatingApp: false, creatingAppErrorMessage: '' };

    case ContainerFlowActionType.HoldFirstBuild:
      return { ...state, step: 2, isCreatingApp: false, creatingAppErrorMessage: '', createdApp: action.payload };
      
    case ContainerFlowActionType.SetGithubRepositories:
      return { ...state, gitHubRepositories: action.payload, isLoadingRepository: false };

    case ContainerFlowActionType.SetFetchGithubRepositoriesError:
      return { ...state, isLoadingRepository: false, loadingRepositoryErrorMsg: action.payload}
    
    // case ContainerFlowActionType.SetContainerName:
    //   return {
    //     ...state,
    //     container: {
    //       ...state.container,
    //       name: action.payload.value
    //     }
    //   }
    
    case ContainerFlowActionType.StartCreatingApp:
      return {
        ...state,
        isCreatingApp: true,
        creatingAppErrorMessage: ''
      }

    case ContainerFlowActionType.FinishCreatingApp:
      return {
        ...state,
        isCreatingApp: false,
        creatingAppErrorMessage: action.payload.errorMessage || ''
      }

    default:
      return state;
  }
}

const NewContainerFlow = () => {
  const { user } = useOutletContext<{ user: User }>();
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { gitHubRepositories, isImporting, importingRepoError, step, loadingRepositoryErrorMsg, isCreatingApp, creatingAppErrorMessage, selectedRepository, isLoadingRepository, isImportingSuccess, showCreditCardRequired, createdApp } = state;
  // Where this app's code comes from. Asked once, at creation: switching an
  // existing app's source is a different question with different consequences,
  // and Settings does not offer it.
  const [source, setSource] = useState<AppSource>('GITHUB');
  /**
   * One query per source, not one shared between them.
   *
   * The box moved into the panel head and started serving both sources, and
   * with a single piece of state a repository name typed on the GitHub side
   * became a Docker Hub search the moment you switched — firing a real query
   * against the registry for a string that was never meant for it.
   *
   * Keyed rather than cleared on switch, so going back to a source you were
   * already searching finds your text where you left it.
   */
  const [searchBySource, setSearchBySource] = useState<Record<AppSource, string>>({
    GITHUB: '',
    DOCKER_HUB: '',
  });
  const searchInputText = searchBySource[source];
  const setSearchInputText = (text: string) =>
    setSearchBySource((prev) => ({ ...prev, [source]: text }));

  const [containerImage, setContainerImage] = useState<ContainerImageValue>(INITIAL_CONTAINER_IMAGE);
  // The resolved image, or null while it is unresolved. Next is gated on this
  // rather than on the fields being filled in — a typo'd tag fills the fields
  // just as well as a real one.
  const [imageInfo, setImageInfo] = useState<ContainerImageInfo | null>(null);
  const navigate = useNavigate();
  
  const code = searchParams.get('code');
  const installationId = searchParams.get('installation_id');
  const setupAction = searchParams.get('setup_action');

  useEffect(() => {
    return () => { dispatch(reset()) };
  }, []);

  useEffect(() => {
    let subscription: Subscription;

    // Nothing to list on the image tab, and subscribing would surface a
    // GitHub error on a screen that never mentions GitHub.
    if (step === 0 && source === 'GITHUB') {
      subscription = back4app2.subscribeToGitHubRepositories((error, snapshot) => {
        if (error) {
          if (error instanceof NetworkError) {
            console.error('network error', error);
            dispatch({ 
              type: ContainerFlowActionType.SetFetchGithubRepositoriesError, 
              payload: 'Network error when loading GitHub repositories. Please check your internet connection or try again later.'
            });
          } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
            window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
          } else {
            console.error('unexpected error loading GitHub repositories', error);
            dispatch({ 
              type: ContainerFlowActionType.SetFetchGithubRepositoriesError, 
              payload: 'Unexpected error when loading GitHub repositories. Please try again later.'
            });
          }
  
          subscription.unsubscribe();
        } else {
          dispatch({
            type: ContainerFlowActionType.SetGithubRepositories,
            payload: snapshot
          });
        }
      });
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }      
    };
  }, [step, source]);

  useEffect(() => {
    
    if (!code || !installationId || !setupAction || !['install', 'update'].includes(setupAction)) {
      if ((window.opener && window.opener !== window) || window.name === 'GithubIntegrationWindow') {
        dispatch({
          type: ContainerFlowActionType.ImportingRepoError,
          payload: 'Something went wrong! Please try again after uninstalling the app.'
        });
        new Promise<void>(res => setTimeout(() => {
          window.close();
          res();
        }, 8_000));
      }
      return;
    }
    
    dispatch({ type: ContainerFlowActionType.StartImportingRepository });

    let searchParamsChanged = false, closeWindow = true, displayError = false;
    
    (async () => {
      try {
        await back4app2.importGitHubInstallation(code, installationId);
      } catch (e) {
        displayError = true;
        if (searchParamsChanged) {
          console.error('error importing GitHub installation', e);
        } else if (e instanceof NetworkError) {
          console.error('network error', e);
          dispatch({
            type: ContainerFlowActionType.ImportingRepoError,
            payload: 'Network error when importing from GitHub. Please check your internet connection or try again later.'
          });
        } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
          window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        } else if (e instanceof GitHubCodeError || e instanceof GitHubInstallationIdError) {
          dispatch({
            type: ContainerFlowActionType.ImportingRepoError,
            payload: 'This GitHub installation is invalid or has expired. Please try again.'
          });
        } else if (e instanceof BlockedResourceError) {
          dispatch({
            type: ContainerFlowActionType.ImportingRepoError,
            payload: e.message
          });
        }  else if (e instanceof CreditCardValidationRequiredError) {
          closeWindow = false;
          dispatch({
            type: ContainerFlowActionType.ShowCardRequired,
            payload: e.message
          });
        } else {
          console.error('unexpected error importing GitHub installation', e);
          dispatch({
            type: ContainerFlowActionType.ImportingRepoError,
            payload: 'Unexpected error when importing from GitHub. Please try again later.'
          });
        }
      }

      if (!searchParamsChanged && !displayError) {
        dispatch({ type: ContainerFlowActionType.ImportingRepoSuccess });
        window.close();
      } else {
        if (closeWindow && !searchParamsChanged) {
          await new Promise(res => setTimeout(res, 8_000));
          window.close();
        }
      }
    })();
    return () => { 
      searchParamsChanged = true;
    };
  }, [code, installationId, setupAction]);

  useEffect(() => {
    step === 0 ? trackEvent(AmplitudeEvent.AT_SELECT_REPOSITORY_PAGE) : trackEvent(AmplitudeEvent.AT_CREATE_APP_PAGE);
  }, [step]);

  const onImportRepository = (repository: GitHubRepository) => {
    dispatch({ type: ContainerFlowActionType.SetSelectedRepository, payload: repository });
  }
  
  const onClickBack = () => {
    // Belt and braces for the same reason the arrow is hidden on step 2: the
    // app already exists, and step 1's submit would create another.
    if (step === 2) {
      return;
    }

    if (step === 0) {
      // navigate(`/apps`);
      window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`
    } else if (step > 0) {
      // Coming back to pick a source starts from the whole list. The search is
      // a narrowing for ONE act of choosing: carrying it back means landing on
      // "where does your app come from?" with most of your repositories
      // hidden, and nothing on screen explaining why only two are there — the
      // box is at the top with a word in it, but you are here to choose
      // differently, not to resume the search you already finished.
      if (state.step - 1 === 0) {
        setSearchBySource({ GITHUB: '', DOCKER_HUB: '' });
      }

      dispatch({ type: ContainerFlowActionType.SetCurrentStep, payload: state.step - 1 });
    }
  }

  const onContainerFormSubmit = async (container: ContainerType) => {
    trackEvent(AmplitudeEvent.CREATE_APP_CLICK);
    dispatch(startCreatingApp());

    let appId: string;

    try {
      if (source === 'DOCKER_HUB') {
        // No branch, no root directory, no build method: this route brings a
        // finished artifact instead of source.
        appId = await back4app2.createAppFromImage(
          containerImage.provider,
          container.name,
          containerImage.repository,
          containerImage.tag,
          container.envVars,
          container.healthCheckEndpoint,
          container.exposedPort,
          // Sent only as a complete pair; the form will not resolve an image
          // with half a credential, so this cannot be half-filled here.
          containerImage.username || undefined,
          containerImage.password || undefined,
          user.maxApps
        );
      } else {
        appId = await back4app2.createAppFromRepository(container.name, container.selectedRepo!.id, container.branch, container.rootDirPath, container.autoDeploy, container.envVars, container.healthCheckEndpoint, container.exposedPort, user.maxApps, container.startCommand, container.builderPreference, container.isStaticSite, container.runtimeVersion);
      }
    } catch (e) {
      if (e instanceof NetworkError) {
        console.error('network error', e);
        dispatch(finishCreatingApp('Network error when creating app. Check your internet connection.'));
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
      } else if (e instanceof DuplicatedError) {
        dispatch(finishCreatingApp('You already have an app with this same name.'));
      } else if (e instanceof NotFoundError) {
        dispatch(finishCreatingApp(source === 'DOCKER_HUB'
          ? 'The image was not found. Check the repository and tag and try again.'
          : 'The GitHub repository was not found. Please try to select the app repository again.'));
      } else if (e instanceof BlockedResourceError) {
        dispatch(finishCreatingApp(e.message));
      } else if (e instanceof UserVerificationPendingError) {
        dispatch(finishCreatingApp('Please verify your email to continue creating new apps.'));
      } else if (e instanceof InvalidInputError) {
        // Names the field and what to change — 'Unexpected error' does not.
        dispatch(finishCreatingApp(e.message));
      } else {
        console.error('unexpected error creating app', e);
        dispatch(finishCreatingApp('Unexpected error when creating app.'));
      }

      return;
    }

    // The app exists now. Whether its first build has started is a property of
    // the app, not of this component - so ask the app, which is also what the
    // overview does after a reload (§3.4). A failure here must not strand the
    // customer in the wizard: go to the overview, which shows the same thing.
    try {
      const app = await back4app2.findAppById(appId);

      if (app?.pendingFirstBuild) {
        dispatch(holdFirstBuild(app));

        return;
      }
    } catch (e) {
      console.error('could not read the app after creating it', e);
    }

    navigate(`/apps/${appId}`);
  };

  // Publishes the first build the analysis held. The panel has already written
  // whatever the customer chose to apply.
  const onFirstBuildRelease = async () => {
    const serviceEnvironmentId =
      createdApp?.pendingFirstBuild?.serviceEnvironmentId;

    if (serviceEnvironmentId) {
      await back4app2.releaseFirstBuild(serviceEnvironmentId);
    }

    navigate(`/apps/${createdApp!.id}`);
  };

  const showContent = (step: number) => {
    switch (step) {
      case 0:
        // Tab bar, then a body that is only the active source's work — the
        // shape the design system uses for a composed screen. The repository
        // list is deliberately full-bleed (padding lives on each row), so the
        // image form supplies its own; `newContainerWrapper` has none.
        return <>
          {/* The question, the two cards and the search now live together in
              the panel's own head. They were split across the card header and
              the body before, which is why the search box had to be kept
              mounted on both sources just to stop the header reflowing — it
              is one box in one place now, and the reflow problem is gone. */}
          {/* Header, source cards and search pin together as one block: the
              search is the control the list below it answers to, and a list
              you have scrolled into with the box gone off the top is a list
              you cannot narrow without scrolling back. One sticky wrapper
              rather than three, so they cannot pin at different offsets and
              overlap each other. */}
          <div className="sticky top-0 z-10 bg-dark-blue rounded-t-[10px]">
            <StepHeader step={1} totalSteps={3} title="Where your apps come from?" sticky={false}>
              Pick a source — the search below always looks inside it.
            </StepHeader>

            <div className="px-[1.875rem] pt-7 pb-7 border-b border-card-line">
              <SourceSelector value={source} onChange={setSource} githubConnected={!!gitHubRepositories?.length} />

              {/* Hidden only where it can do nothing: GitHub with no
                  repositories at all has nothing to filter, and an empty box
                  there reads as a broken search rather than as an account with
                  no access yet. */}
              {source === 'DOCKER_HUB' || gitHubRepositories?.length ? (
                <div className="mt-5 flex items-center gap-2.5 h-[50px] px-4 rounded-xl bg-field-blue border border-card-line focus-within:border-regal-blue transition-colors">
                  <SearchIcon className="flex-none opacity-70" />
                  <input
                    className="flex-1 min-w-0 bg-transparent border-0 outline-none text-white text-sm placeholder:text-text-faint"
                    placeholder={source === 'DOCKER_HUB' ? 'Search images on Docker Hub — e.g. nginx, acme/api' : 'Search your GitHub repositories'}
                    value={searchInputText}
                    onChange={(e) => setSearchInputText(e.target.value)}
                  />
                  {/* Only for the list this component owns. The Docker Hub
                      count lives inside ContainerImageSource, which is what
                      performs that search — reporting it from here would mean
                      lifting the result set out of the component that fetches
                      it, to save the eye a few centimetres. */}
                  {source === 'GITHUB' && !!gitHubRepositories?.length && (
                    <span className="flex-none text-xs text-text-faint">
                      {visibleRepositoryCount} {visibleRepositoryCount === 1 ? 'result' : 'results'}
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {/* Keyed on the source so switching replays the enter animation —
              the body arrives with the cards instead of snapping ahead. */}
          <div
            key={source}
            id="source-panel"
            role="tabpanel"
            className="source-panel-enter border-t border-card-line"
            aria-labelledby={source === 'GITHUB' ? 'source-tab-github' : 'source-tab-image'}
          >
            {source === 'GITHUB'
              ? <IntegrateGithub githubRepos={gitHubRepositories} isLoadingRepository={isLoadingRepository} isImporting={isImporting} errorMsg={importingRepoError || loadingRepositoryErrorMsg} onImport={onImportRepository} filterText={searchInputText} />
              : <ContainerImageSource
                  value={containerImage}
                  onChange={setContainerImage}
                  onResolved={setImageInfo}
                  onNext={() => dispatch({ type: ContainerFlowActionType.SetCurrentStep, payload: 1 })}
                  searchQuery={searchInputText || ''}
                />}
          </div>
        </>
      case 1:
        return <CreateContainerForm isCreatingApp={isCreatingApp} creatingAppErrorMessage={creatingAppErrorMessage} onCancel={onClickBack} onSubmit={onContainerFormSubmit} selectedRepository={selectedRepository} source={source} />
      case 2:
        // No Cancel here. The app is already created — the only way out is
        // forward, and pretending otherwise would suggest the app can be
        // un-made by walking back.
        return createdApp?.mainService?.mainServiceEnvironment ? (
          // Design-system card padding (22/28, scaled up for a full-width
          // step) instead of the creation form's 40/70, which was built for a
          // narrower column.
          <div className="px-8 py-7">
            <PreDeployPanel
              appId={createdApp.id}
              serviceEnvironment={createdApp.mainService.mainServiceEnvironment}
              runAssistance={createdApp.pendingFirstBuild?.runAssistance}
              proceedLabel="Save and start"
              skipLabel="Start without changes"
              autoProceedSeconds={90}
              onProceed={onFirstBuildRelease}
            />
          </div>
        ) : null
      default:
        return <div>Ooops!! sorry 😜</div>
    }
  }

  if (isImporting || (isImportingSuccess) || importingRepoError) {
    let content;
    if (isImportingSuccess) {
      content = <div className="flex h-full justify-center items-start flex-col">
        <h1 className="font-sora text-3xl text-center mb-4">Github Installation Completed</h1>
        <div className="font-inter text-sm text-left">This window will close automatically.</div>
      </div>
    } else if (importingRepoError) {
      content = <div className="flex h-full justify-center items-start flex-col">
        <h1 className="font-sora text-3xl mb-4">{importingRepoError}</h1>
        <div className="font-inter text-sm">This window will close automatically in 8 seconds.</div>
      </div>
      if (showCreditCardRequired) {
        content = <div className="flex h-full justify-center items-start flex-col pl-4">
          <h1 className="font-sora text-3xl mb-4">Please validate your credit card to proceed.</h1>
          <div className="w-full">
            <Button type='primary' onClick={() => window.close()}><a role="button" target="_blank" rel='noreferrer noopener' className='font-semibold text-sm' href={`https://checkout.back4app.io/subscription/0EeFjudf6H`}>Validate Now</a></Button>
            <Button type='secondary' onClick={() => window.close()} className='text-sm border-[#fafafa33] font-medium ml-4'>Cancel</Button>
          </div>
        </div>
      }
    } else {
      content = <div className="flex h-full justify-center items-center flex-col">
        <div className="flex justify-center items-center overflow-visible h-6 mb-4 z-[-1]">
          <GithubIcon width="24px" height="24px" />
          <div className="flex space-x-2 ml-5">
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-dot-flashing"></span>
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-[dot-flashing_1s_infinite_0.5s_alternate] "></span>
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-[dot-flashing_1s_infinite_1s_alternate] "></span>
          </div>
          <B4AIconSVG width="76px" height="76px" />
        </div>
        <div className="font-sora font-semibold text-lg leading-140 text-center">Importing repositories...</div>
      </div>
    }
    return <div className="absolute w-full h-full top-0 left-0 bottom-0 right-0 bg-dark-blue z-20 p-4">
      {content}
    </div>
  }
  // What the stepper shows under each label, once the choice behind it exists.
  // Written here rather than in the stepper so that stays a dumb renderer and
  // every string describing this flow lives next to the state it reads.
  // Same predicate the list itself runs, imported rather than repeated.
  const visibleRepositoryCount = filterRepositories(gitHubRepositories || [], searchInputText).length;

  return <div className='relative overflow-y-auto'>
    <div className={styles.container}>
      <div className={styles.header}>
        <img src={newContainerLogo} alt="create new container" width="20" height="20" />
        <h1 className={styles.headerText}>Deploy a Web Application</h1>
      </div>

      {/* One line for the whole flow, instead of one per step. The rail now
          says which step you are on, so re-stating it here was the same
          information twice — and the step-specific wording was what made the
          heading and the sentence under it disagree on the image route. */}

      <div className="mt-6">
        <div className={`${styles.newContainerWrapper} ${step === 2 ? ' min-h-[300px]' : ''}`}>
            {/* Same head on every step, and the source it is working on moved
                out of the right-hand corner into the line under the title.
                On the right it was a second thing to find; here it sits where
                the first step puts its explanation, answering the same
                question — what is this step about? — with a fact. */}
            {step === 0 ? null : (
              <StepHeader
                step={step === 2 ? 3 : 2}
                totalSteps={3}
                title={step === 2 ? 'Before we build' : 'Review what we filled in'}
                // No way back from step 3: the app EXISTS by then. Going back
                // returns to a form whose Create button would make a second
                // one, and the person has no reason to suspect that — they are
                // still setting the first up.
                onBack={step === 2 ? undefined : onClickBack}
              >
                <span className="flex items-center gap-2 flex-wrap">
                  {source === 'DOCKER_HUB' ? (
                    <>
                      <DockerLogo width="15" height="15" style={{ color: DOCKER_LOGO.color }} />
                      <span className="font-courrier">{containerImage.repository}:{containerImage.tag}</span>
                      {/* The digest is the point: a tag is a moving pointer,
                          and this is what says which bytes are about to be
                          deployed. */}
                      {imageInfo?.digest && (
                        <>
                          <span className="text-text-faint">·</span>
                          <span className="font-courrier text-text-faint">{imageInfo.digest.slice(0, 19)}…</span>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <GithubIcon width="15px" height="15px" />
                      <span className="font-courrier">{selectedRepository?.fullName}</span>
                      <span className="text-text-faint">·</span>
                      <span className="font-courrier text-text-faint">{selectedRepository?.defaultBranch}</span>
                    </>
                  )}
                </span>
              </StepHeader>
            )}
          {showContent(step)}
        </div>
      </div>
    </div>
  </div>;
};

export default NewContainerFlow;
